import { useState } from 'react'

import { motion } from 'framer-motion'
import { BillingAddonReferenceFragment } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useJoinOneWaitlist from 'modules/upsell/hooks/useJoinOneWaitlist'

export const OneLandingButton = ({
  buttonLabel,
  variant = 'primary',
  billingAddon,
  moduleName,
  componentName,
  onClick,
}: {
  buttonLabel: string | null | undefined
  variant: 'primary' | 'secondary'
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  componentName: string
  onClick?: () => void
}) => {
  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false)
  const joinWaitlist = useJoinOneWaitlist({
    billingAddon: billingAddon ? billingAddon : null,
    moduleName: moduleName,
  })

  const handleWaitlistJoin = async () => {
    if (onClick) {
      onClick()
    } else {
      if (isWaitlistLoading) return

      try {
        track(trackingEvents.upsell.clickOneWaitlistButton, {
          source: `one-landing-page-${componentName}`,
        })

        setIsWaitlistLoading(true)

        const result = await joinWaitlist()

        if (result) {
          track(trackingEvents.upsell.joinedOneWaitlist, {
            source: `one-landing-page-${componentName}`,
          })
          app.setFlash({
            type: 'correct',
            message: i18n.t('growth.one_waitlist.animation.join_waitlist.success'),
          })
          window.location.reload()
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('growth.one_waitlist.animation.join_waitlist.error'),
          })
        }
      } catch (error) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.one_waitlist.animation.join_waitlist.error'),
        })
      } finally {
        setIsWaitlistLoading(false)
      }
    }
  }

  const Spinner = () => (
    <motion.div
      style={{
        width: '24px',
        height: '24px',
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
      }}
      animate={{ rotate: 360 }}
      transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  )

  switch (variant) {
    case 'primary':
      return (
        <button
          style={{
            color: 'white',
            background:
              variant === 'primary'
                ? 'linear-gradient(270deg, rgba(161, 173, 229, 0.7) 0%, rgba(229, 25, 67, 0.7) 50%, rgba(229, 86, 25, 0.7) 100%)'
                : 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            height: '32px',
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1'
          }}
          onClick={handleWaitlistJoin}
        >
          {isWaitlistLoading ? <Spinner /> : buttonLabel}
        </button>
      )

    case 'secondary':
      return (
        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(161, 173, 229, 0.7) 0%, rgba(229, 25, 67, 0.7) 50%, rgba(229, 86, 25, 0.7) 100%)',
            borderRadius: '6px',
            padding: '1px',
            width: 'fit-content',
          }}
        >
          <button
            style={{
              color: '#333',
              background: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              height: '32px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
            onClick={handleWaitlistJoin}
          >
            {isWaitlistLoading ? <Spinner /> : buttonLabel}
          </button>
        </div>
      )
  }
}
